import React, { useState, useEffect, useRef } from 'react';
import { getManagementPendingOrders, getManagementOrderById, getClientById } from '../services/api';
import { formatDateTime, getStatusColor, handleApiError, SCRATCH_CARD_PRICES, EPIN_PRICES } from '../utils/helpers';
import ManagementOrderDetailsView from './ManagementOrderDetailsView';
import SuccessModal from '../components/common/SuccessModal';
import DateRangePicker from '../components/common/DateRangePicker';
import WholesalerName from '../components/common/WholesalerName';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ManagementAllOrders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]); // Store original page data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [dealerFilter, setDealerFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [tableLoading, setTableLoading] = useState(false);
    const [dealers, setDealers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [totalFromAPI, setTotalFromAPI] = useState(0); // Store total from API
    const [itemsPerPage] = useState(20);
    const [searchLoading, setSearchLoading] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [exporting, setExporting] = useState(false);
    const [allOrders, setAllOrders] = useState([]);
    const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);


    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ orderId: '', action: '', status: '' });

    // Refs for debouncing and request cancellation
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    const navigate = useNavigate();
    const { accessToken } = useAuth();

    // Calculate pagination for filtered results
    const calculatePagination = (filteredData) => {
        const total = filteredData.length;
        const pages = Math.ceil(total / itemsPerPage);
        setTotalPages(pages);
        setTotalElements(total);

        // Reset to page 1 if current page is beyond new total pages
        if (page > pages && pages > 0) {
            setPage(1);
        }
    };

    // Get current page data - slice when using client-side pagination (date filter or active search)
    const getCurrentPageData = () => {
        const isSearchActive = searchQuery && searchQuery.trim().length > 0;
        if (exportStartDate || exportEndDate || isSearchActive) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            return filteredOrders.slice(startIndex, endIndex);
        }
        return orders;
    };

    const applyDateFilter = (list) => {
        if (!exportStartDate && !exportEndDate) return list;
        const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
        const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
        return list.filter((o) => {
            const created = new Date(o.createdAt);
            if (start && created < start) return false;
            if (end && created > end) return false;
            return true;
        });
    };

    const applyAllFilters = (list) => {
        let out = list;
        if (dealerFilter !== 'all') {
            out = out.filter((order) => (order.assignedDealer || order.dealerName) === dealerFilter);
        }
        out = applyDateFilter(out);
        return out;
    };

    // Fetch all orders (paginated)
    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Use the same endpoint but with status=ALL
            const data = await getManagementPendingOrders({ page: page - 1, size: 20, status: 'ALL' });
            const pageOrders = data.content || [];
            setOrders(pageOrders);
            setOriginalOrders(pageOrders); // Store original page data
            if (!exportStartDate && !exportEndDate) {
                setFilteredOrders(applyAllFilters(pageOrders));
            }
            if (!exportStartDate && !exportEndDate) {
                setTotalPages(Number(data.totalPages) || 1);
                setTotalElements(Number(data.totalElements) || 0);
                setTotalFromAPI(Number(data.totalElements) || 0); // Update totalFromAPI
            }

            // Update dealers list with new dealers from this page
            const newDealers = pageOrders.map(o => o.assignedDealer || o.dealerName).filter(Boolean);
            setDealers(prevDealers => {
                const combined = [...prevDealers, ...newDealers];
                return Array.from(new Set(combined)); // Remove duplicates
            });
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setError('Failed to fetch all orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!exportStartDate && !exportEndDate) {
            fetchOrders();
        }
        // eslint-disable-next-line
    }, [accessToken, page, exportStartDate, exportEndDate]);

    // Apply dealer filter when original orders change
    useEffect(() => {
        if (exportStartDate || exportEndDate) {
            const next = applyAllFilters(allOrdersLoaded ? allOrders : originalOrders);
            setFilteredOrders(next);
            calculatePagination(next);
        } else {
            const next = applyAllFilters(originalOrders);
            setFilteredOrders(next);
            setTotalElements(next.length);
            setTotalPages(Number(totalPages) || 1);
        }
    }, [originalOrders, dealerFilter]);

    // Utility: check if query is likely an order ID (all digits or starts with 'ORD-')
    const isOrderId = (query) => /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

    // Synchronous input handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300); // 300ms delay
    };

    // Perform the actual search
    const performSearch = async (query) => {
        if (!query || !query.trim()) {
            let filtered = orders;
            if (exportStartDate || exportEndDate) {
                filtered = allOrdersLoaded ? allOrders : originalOrders;
                filtered = applyAllFilters(filtered);
                setFilteredOrders(filtered);
                calculatePagination(filtered);
                setPage(1);
            } else {
                filtered = applyAllFilters(originalOrders);
                setFilteredOrders(filtered);
                setTotalElements(totalFromAPI);
                setTotalPages(Math.ceil(totalFromAPI / itemsPerPage) || 1);
                setPage(1);
            }
            setSearchLoading(false);
            return;
        }

        // Check if it's a numeric search (all digits or starts with 'ORD-')
        const isNumericSearch = /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

        if (isNumericSearch) {
            setSearchLoading(true);
            try {
                const order = await getManagementOrderById(query, 'ALL');
                const aborted = abortControllerRef.current?.signal.aborted;
                if (!aborted) {
                    if (order && order.id) {
                        const dealerOk = dealerFilter === 'all' || (order.assignedDealer || order.dealerName) === dealerFilter;
                        const created = new Date(order.createdAt);
                        const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
                        const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
                        const dateOk = (!start || created >= start) && (!end || created <= end);
                        if (dealerOk && dateOk) {
                            setFilteredOrders([order]);
                            setTotalElements(1);
                            setTotalPages(1);
                            setPage(1);
                        } else {
                            setFilteredOrders([]);
                            setTotalElements(0);
                            setTotalPages(1);
                            setPage(1);
                        }
                    } else {
                        setFilteredOrders([]);
                        setTotalElements(0);
                        setTotalPages(1);
                        setPage(1);
                    }
                }
            } catch (err) {
                const aborted = abortControllerRef.current?.signal.aborted;
                if (!aborted) {
                    setFilteredOrders([]);
                    setTotalElements(0);
                    setTotalPages(1);
                    setPage(1);
                }
            } finally {
                setSearchLoading(false);
            }
        } else {
            // Client-side filtering for non-numeric queries
            let filtered = orders;
            if (exportStartDate || exportEndDate) {
                filtered = allOrdersLoaded ? allOrders : originalOrders;
            }
            filtered = filtered.filter(order =>
                String(order.id).toLowerCase().includes(query.toLowerCase())
            );
            filtered = applyAllFilters(filtered);
            setFilteredOrders(filtered);
            calculatePagination(filtered); // Recalculate pagination for client-side filtered data
            setPage(1);
            setSearchLoading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleDealerFilterChange = (e) => {
        const newFilter = e.target.value;
        setDealerFilter(newFilter);

        // Apply dealer filter to current data
        let source = originalOrders;
        if (exportStartDate || exportEndDate) {
            source = allOrdersLoaded ? allOrders : originalOrders;
        }
        const next = applyAllFilters(source);
        setFilteredOrders(next);
        calculatePagination(next);
        setPage(1); // Reset to first page when filter changes
    };



    const collectAllOrders = async (startDate) => {
        const out = [];
        let p = 0;
        let lastTotalPages = 1;
        const size = 500; // Increased batch size to reduce requests
        setLoading(true);
        try {
            while (true) {
                const data = await getManagementPendingOrders({ page: p, size, status: 'ALL' });
                const content = data?.content || [];
                if (content.length === 0) break;
                out.push(...content);

                // Stop early if we reached past the start date
                if (startDate) {
                    const start = new Date(`${startDate}T00:00:00`);
                    // Check the last order in the batch (assuming descending order)
                    const lastOrder = content[content.length - 1];
                    if (lastOrder && new Date(lastOrder.createdAt) < start) {
                        break;
                    }
                }

                lastTotalPages = Number(data?.totalPages || 1);
                if (p >= lastTotalPages - 1) break;
                p++;
            }
        } catch (err) {
            console.error("Error collecting all orders:", err);
            setError("Failed to load all orders for filtering");
        } finally {
            setLoading(false);
        }
        return out;
    };

    useEffect(() => {
        const run = async () => {
            if (exportStartDate || exportEndDate) {
                setLoading(true);
                // Always fetch to ensure we have the correct range (optimization: stop early)
                // We don't rely on allOrdersLoaded because the range might have changed
                const all = await collectAllOrders(exportStartDate);
                setAllOrders(all);
                setAllOrdersLoaded(true);
                const sourceOrders = all;

                const base = dealerFilter === 'all' ? sourceOrders : sourceOrders.filter((o) => (o.assignedDealer || o.dealerName) === dealerFilter);
                const next = applyDateFilter(base);
                setFilteredOrders(next);
                calculatePagination(next);
                setPage(1);
                setLoading(false);
            } else {
                // When clearing date filter, we go back to server-side pagination
                // We don't need to do anything here as the other useEffect handles !exportStartDate && !exportEndDate
                setAllOrders([]);
                setAllOrdersLoaded(false);
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exportStartDate, exportEndDate, dealerFilter]);

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            const details = await getManagementOrderById(orderId, 'ALL');
            setSelectedOrder(details);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handleOrderUpdate = (orderId, newStatus, successData = null) => {
        // Remove the updated order from the filtered orders list
        setFilteredOrders(prev => prev.filter(order => order.id !== orderId));
        setTotalElements(prev => prev - 1);

        // Close the details view
        setSelectedOrder(null);
        setDetailsError(null);

        // Show success modal if success data is provided
        if (successData) {
            setSuccessModalData(successData);
            setShowSuccessModal(true);
        }

        // Show success message (optional)

    };

    const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
    const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessModalData({ orderId: '', action: '', status: '' });
    };

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            const toDateObj = (d) => (d ? new Date(`${d}T23:59:59`) : null);
            const fromDateObj = (d) => (d ? new Date(`${d}T00:00:00`) : null);
            const start = fromDateObj(exportStartDate);
            const end = toDateObj(exportEndDate);
            const csvEscape = (v) => {
                const s = v == null ? '' : String(v).replace(/"/g, '""');
                return `"${s}"`;
            };

            const warehouseMap = {
                'EBL_WH': 'ERBIL',
                'DUH_WH': 'DUHOK',
                'SUL_WH': 'SULAYMANIYAH',
                'KRK_WH': 'KIRKUK',
                'MSL_WH': 'MOSUL',
                'BGD_WH': 'BAGHDAD'
            };
            const getCityName = (wh) => warehouseMap[wh] || wh;

            const collectAll = async () => {
                const out = [];
                let p = 0;
                let lastTotalPages = 1;
                const size = 500; // Increased batch size
                while (true) {
                    // Pass date filters to the API for export as well
                    const data = await getManagementPendingOrders({
                        page: p,
                        size,
                        status: 'ALL',
                        startDate: exportStartDate,
                        endDate: exportEndDate
                    });
                    const content = data?.content || [];
                    out.push(...content);
                    lastTotalPages = Number(data?.totalPages || 1);
                    if (p >= lastTotalPages - 1) break;
                    p++;
                }
                return out;
            };
            const source = await collectAll();
            const filtered = source.filter((o) => {
                const created = new Date(o.createdAt);
                if (start && created < start) return false;
                if (end && created > end) return false;
                return true;
            });
            const detailsList = filtered; // Use filtered list directly
            const epinClientIds = new Set();
            detailsList.forEach((detail) => {
                if (detail && Array.isArray(detail.epinOrders)) {
                    detail.epinOrders.forEach((ep) => { if (ep.clientId) epinClientIds.add(ep.clientId); });
                }
            });
            const clientNameMap = {};
            await Promise.all(Array.from(epinClientIds).map(async (cid) => {
                try {
                    const c = await getClientById(cid);
                    clientNameMap[cid] = (c && (c.name || c.fullName)) || '';
                } catch (e) {
                    clientNameMap[cid] = '';
                }
            }));

            const allDenoms = new Set([
                ...Object.keys(SCRATCH_CARD_PRICES),
                ...Object.keys(EPIN_PRICES)
            ]);
            const scratchAgg = {};
            const epinAgg = {};
            const topupAgg = {};

            detailsList.forEach((detail, idx) => {
                const o = detail; // detail is the order object now
                const dealer = o.assignedDealer || o.dealerName || '';
                if (detail && Array.isArray(detail.scratchCardOrders) && detail.scratchCardOrders.length > 0) {
                    detail.scratchCardOrders.forEach((item) => {
                        const denom = item.denomination || '';
                        const qty = Number(item.quantity || 0);
                        const warehouse = item.warehouse || '';
                        if (denom) allDenoms.add(denom);
                        const key = `${o.id}|${dealer}|${warehouse}`;
                        if (!scratchAgg[key]) scratchAgg[key] = { orderId: o.id, dealer, warehouse, counts: {}, total: 0, dateMin: null };
                        scratchAgg[key].counts[denom] = (scratchAgg[key].counts[denom] || 0) + qty;
                        const price = SCRATCH_CARD_PRICES[denom] || Number(denom) || 0;
                        scratchAgg[key].total += (price * qty) || 0;
                        const created = new Date(o.createdAt);
                        if (!scratchAgg[key].dateMin || created < scratchAgg[key].dateMin) scratchAgg[key].dateMin = created;
                    });
                }
                if (detail && Array.isArray(detail.epinOrders) && detail.epinOrders.length > 0) {
                    detail.epinOrders.forEach((item) => {
                        const denom = item.denomination || '';
                        const qty = Number(item.quantity || 0);
                        const epClientId = item.clientId || o.clientId || '';
                        const epClientName = clientNameMap[epClientId] || '';
                        if (denom) allDenoms.add(denom);
                        const key = `${o.id}|${dealer}|${epClientId}`;
                        if (!epinAgg[key]) epinAgg[key] = { orderId: o.id, dealer, clientName: epClientName, counts: {}, total: 0, dateMin: null };
                        epinAgg[key].counts[denom] = (epinAgg[key].counts[denom] || 0) + qty;
                        const price = EPIN_PRICES[denom] || Number(denom) || 0;
                        epinAgg[key].total += (price * qty) || 0;
                        const created = new Date(o.createdAt);
                        if (!epinAgg[key].dateMin || created < epinAgg[key].dateMin) epinAgg[key].dateMin = created;
                    });
                }
                const eTopUp = detail && (detail.eTopUp || detail.etopUpOrder);
                const hasTopUp = (eTopUp && eTopUp.amount) || (detail && detail.etopUpAmount);
                if (hasTopUp) {
                    const amount = (eTopUp && eTopUp.amount) || detail.etopUpAmount || 0;
                    const totalWithFee = detail.etopUpAmountToBePaid || Math.round(amount * 1.025);
                    const key = `${o.id}|${dealer}`;
                    if (!topupAgg[key]) topupAgg[key] = { orderId: o.id, dealer, amountTotal: 0, payableTotal: 0, count: 0, dateMin: null };
                    topupAgg[key].amountTotal += Number(amount) || 0;
                    topupAgg[key].payableTotal += Number(totalWithFee) || 0;
                    topupAgg[key].count += 1;
                    const created = new Date(o.createdAt);
                    if (!topupAgg[key].dateMin || created < topupAgg[key].dateMin) topupAgg[key].dateMin = created;
                }
            });

            const sortDenoms = (arr) => {
                return arr.sort((a, b) => {
                    const na = Number(a);
                    const nb = Number(b);
                    if (!isNaN(na) && !isNaN(nb)) return na - nb;
                    return String(a).localeCompare(String(b));
                });
            };
            const denomCols = sortDenoms(Array.from(allDenoms));
            const headers = [
                'Order ID',
                'Order Type',
                'Dealer',
                'Client Name',
                ...denomCols.map((d) => String(d)),
                'E-topup amount',
                'Total',
                'Amount to be paid',
                'Date',
                'Wharehouse'
            ];

            const rows = [];
            Object.keys(scratchAgg).forEach((key) => {
                const item = scratchAgg[key];
                const dealerEsc = csvEscape(item.dealer);
                const orderIdEsc = csvEscape(item.orderId);
                const warehouseEsc = csvEscape(getCityName(item.warehouse || ''));
                const cells = [orderIdEsc, csvEscape('SCRATCH_CARD'), dealerEsc, csvEscape('')];
                let faceTotal = 0;
                denomCols.forEach((d) => {
                    const v = item.counts[d] || 0;
                    cells.push(csvEscape(v ? v : ''));
                    faceTotal += v * Number(d);
                });
                const grand = item.total || 0;
                const dateStr = item.dateMin ? formatDateTime(item.dateMin) : '';
                cells.push(csvEscape(''), csvEscape(faceTotal), csvEscape(grand), csvEscape(dateStr), warehouseEsc);
                rows.push(cells);
            });
            Object.keys(epinAgg).forEach((key) => {
                const item = epinAgg[key];
                const dealerEsc = csvEscape(item.dealer);
                const clientEsc = csvEscape(item.clientName || '');
                const orderIdEsc = csvEscape(item.orderId);
                const cells = [orderIdEsc, csvEscape('EPIN'), dealerEsc, clientEsc];
                let faceTotal = 0;
                denomCols.forEach((d) => {
                    const v = item.counts[d] || 0;
                    cells.push(csvEscape(v ? v : ''));
                    faceTotal += v * Number(d);
                });
                const grand = item.total || 0;
                const dateStr = item.dateMin ? formatDateTime(item.dateMin) : '';
                cells.push(csvEscape(''), csvEscape(faceTotal), csvEscape(grand), csvEscape(dateStr), csvEscape(''));
                rows.push(cells);
            });
            Object.keys(topupAgg).forEach((key) => {
                const t = topupAgg[key];
                const dealerEsc = csvEscape(t.dealer);
                const orderIdEsc = csvEscape(t.orderId);
                const cells = [orderIdEsc, csvEscape('ETOPUP'), dealerEsc, csvEscape('')];
                denomCols.forEach(() => cells.push(csvEscape('')));
                const dateStr = t.dateMin ? formatDateTime(t.dateMin) : '';
                cells.push(csvEscape(t.amountTotal || 0), csvEscape(t.amountTotal || 0), csvEscape(t.payableTotal || 0), csvEscape(dateStr), csvEscape(''));
                rows.push(cells);
            });

            const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const name = `orders_${exportStartDate || 'all'}_${exportEndDate || 'all'}.csv`;
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
        } finally {
            setExporting(false);
        }
    };


    if (error) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Orders Collection</h1>
                <p className="text-gray-600">View and manage all orders from all dealers.</p>
            </div>
            {selectedOrder ? (
                <div>
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    {detailsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : detailsError ? (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                    ) : (
                        <ManagementOrderDetailsView order={selectedOrder} onOrderUpdate={handleOrderUpdate} />
                    )}
                </div>
            ) : (
                <>
                    {/* Filters */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search Orders</label>
                                <input
                                    type="text"
                                    placeholder="Search by Order ID..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {searchLoading && (
                                    <div className="mt-2 text-sm text-gray-500">Searching...</div>
                                )}
                            </div>
                            <div className="md:w-64">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                                <DateRangePicker
                                    value={{ startDate: exportStartDate, endDate: exportEndDate }}
                                    onApply={(s, e) => {
                                        setExportStartDate(s);
                                        setExportEndDate(e);
                                    }}
                                    className="w-full"
                                />
                            </div>
                            <div className="md:w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Dealer</label>
                                <select
                                    value={dealerFilter}
                                    onChange={handleDealerFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">All Dealers</option>
                                    {dealers.map((dealer) => (
                                        <option key={dealer} value={dealer}>{dealer}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* Results count */}
                    <div className="text-sm text-gray-600 mb-4">
                        Showing {getCurrentPageData().length} of {totalElements} orders
                    </div>
                    <div className="flex items-end justify-end gap-3 mb-4">
                        {exporting ? (
                            <div className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center justify-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={handleExportCSV}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                            >
                                Export CSV
                            </button>
                        )}
                    </div>
                    {(loading || tableLoading) ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                            <p className="text-gray-500 text-lg">No orders found</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {getCurrentPageData().map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>{order.assignedDealer || order.dealerName || '-'}</div>
                                                    {order.wholesalerId && (
                                                        <WholesalerName wholesalerId={order.wholesalerId} />
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(order.createdAt)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={() => handleViewDetails(order.id)}>View Details</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {/* Pagination Bar */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 mb-2 w-full">
                            <button
                                onClick={handlePrevPage}
                                disabled={page === 1}
                                className={`flex items-center px-3 py-1 border rounded mr-2 text-base font-medium transition-colors duration-200 ${page === 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                style={{ minWidth: 80 }}
                            >
                                <span className="mr-1">&#8592;</span> Prev
                            </button>
                            <div className="flex space-x-2 justify-center flex-1">
                                {(() => {
                                    const pages = [];
                                    if (totalPages <= 5) {
                                        for (let p = 1; p <= totalPages; p++) {
                                            pages.push(p);
                                        }
                                    } else {
                                        if (page <= 3) {
                                            pages.push(1, 2, 3, 4, '...', totalPages);
                                        } else if (page >= totalPages - 2) {
                                            pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                                        } else {
                                            pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                                        }
                                    }
                                    return pages.map((p, idx) =>
                                        p === '...'
                                            ? <span key={"ellipsis-" + idx} className="w-7 h-7 flex items-center justify-center text-base text-gray-400">...</span>
                                            : <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                className={`w-7 h-7 rounded text-base font-medium transition-colors duration-200 ${p === page ? 'bg-gray-200 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                                            >
                                                {p}
                                            </button>
                                    );
                                })()}
                            </div>
                            <button
                                onClick={handleNextPage}
                                disabled={page === totalPages}
                                className={`flex items-center px-3 py-1 border rounded ml-2 text-base font-medium transition-colors duration-200 ${page === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                style={{ minWidth: 80 }}
                            >
                                Next <span className="ml-1">&#8594;</span>
                            </button>
                        </div>
                    )}
                </>
            )}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={handleCloseSuccessModal}
                orderId={successModalData.orderId}
                action={successModalData.action}
                status={successModalData.status}
            />
        </div>
    );
};

export default ManagementAllOrders;
